// Iter 82.6 / Phase E2.2 — Anomalies Inbox: the operational queue.
// Surfaces all 9 anomaly types (per-day + rolling), color-coded by
// severity. Header KPIs: Open · Pending Regularization · Critical ·
// Overdue Regularizations · Today's New. Row actions: View Snapshot,
// Create Regularization, Acknowledge. Bulk-select drives bulk-ack.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Warning, ArrowsClockwise, CheckCircle, Eye, ArrowSquareOut,
    Clock, MapPin, FileText, Calendar,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";

const SEVERITY_CLS = {
    Critical: "bg-red-100 text-red-700 border-red-200",
    High: "bg-orange-100 text-orange-700 border-orange-200",
    Med: "bg-amber-100 text-amber-700 border-amber-200",
    Low: "bg-slate-100 text-slate-600 border-slate-200",
    Action: "bg-sky-100 text-sky-700 border-sky-200",
};

const STATUS_CLS = {
    open: "bg-amber-100 text-amber-700",
    acknowledged: "bg-emerald-100 text-emerald-700",
    regularization_pending: "bg-sky-100 text-sky-700",
    regularized: "bg-violet-100 text-violet-700",
    closed: "bg-slate-100 text-slate-500",
};

const TYPE_LABEL = {
    absent_on_working_day: "Absent on Working Day",
    missing_check_in: "Missing Check-In",
    missing_check_out: "Missing Check-Out",
    missing_break_end: "Missing Break End",
    late_arrival: "Late Arrival",
    early_departure: "Early Departure",
    unclosed_break: "Unclosed Break",
    consecutive_late_warning: "Consecutive Lates",
    consecutive_absent_warning: "Consecutive Absents",
};

export default function AttendanceAnomaliesInbox() {
    const { can } = useAuth();
    const navigate = useNavigate();
    const [meta, setMeta] = useState({ anomaly_types: [], anomaly_severities: [] });
    const [hrMeta, setHrMeta] = useState({ departments: [] });
    const [employees, setEmployees] = useState([]);
    const [summary, setSummary] = useState(null);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [scope, setScope] = useState("team");
    const [filters, setFilters] = useState({
        anomaly_type: "", severity: "", status_filter: "open",
        from_date: "", to_date: "", department: "",
    });
    const [selected, setSelected] = useState(new Set());
    const [page, setPage] = useState(1);
    const [ackOpen, setAckOpen] = useState(false);
    const [ackRemarks, setAckRemarks] = useState("");
    const [ackTarget, setAckTarget] = useState(null); // null = bulk, else single anomaly

    const reloadRefs = useCallback(async () => {
        try {
            const [m, hm, e] = await Promise.all([
                api.get("/attendance/events/meta"),
                api.get("/hr/employee/meta"),
                api.get("/hr/employee/list", { params: { limit: 500 } }),
            ]);
            setMeta(m.data);
            setHrMeta(hm.data || {});
            setEmployees(e.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load reference data"));
        }
    }, []);

    const reloadSummary = useCallback(async () => {
        try {
            const { data } = await api.get("/attendance/anomalies/summary", { params: { scope } });
            setSummary(data);
        } catch (err) {
            // Don't toast on summary fail — it's a header decoration.
        }
    }, [scope]);

    const reloadRows = useCallback(async () => {
        try {
            const params = { scope, page, page_size: 50 };
            for (const [k, v] of Object.entries(filters)) {
                if (v) params[k] = v;
            }
            const { data } = await api.get("/attendance/anomalies/inbox", { params });
            setRows(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load inbox"));
        }
    }, [scope, filters, page]);

    useEffect(() => { Promise.resolve().then(() => { reloadRefs(); }); }, [reloadRefs]);
    useEffect(() => { Promise.resolve().then(() => { reloadSummary(); reloadRows(); }); }, [reloadSummary, reloadRows]);

    const toggleSel = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelected(next);
    };
    const toggleSelAll = () => {
        if (selected.size === rows.length) setSelected(new Set());
        else setSelected(new Set(rows.map((r) => r.id)));
    };

    const doAck = async () => {
        if (!ackRemarks.trim() || ackRemarks.trim().length < 3) {
            toast.error("Remarks are mandatory (≥ 3 characters).");
            return;
        }
        try {
            if (ackTarget) {
                // Single
                await api.post(`/attendance/anomalies/${ackTarget.id}/acknowledge`, { remarks: ackRemarks });
                toast.success("Anomaly acknowledged.");
            } else {
                // Bulk
                const ids = [...selected];
                if (ids.length === 0) {
                    toast.error("No anomalies selected.");
                    return;
                }
                if (ids.length > 50) {
                    toast.error("Bulk acknowledge limited to 50 at a time.");
                    return;
                }
                const { data } = await api.post("/attendance/anomalies/bulk-acknowledge", {
                    anomaly_ids: ids, remarks: ackRemarks,
                });
                toast.success(`Acknowledged ${data.acknowledged} of ${data.total}.`);
                setSelected(new Set());
            }
            setAckOpen(false); setAckRemarks(""); setAckTarget(null);
            reloadRows(); reloadSummary();
        } catch (err) {
            toast.error(formatApiError(err, "Acknowledge failed"));
        }
    };

    const goRegularize = (row) => {
        // Deep-link to the new request form with anomaly_id + date pre-filled
        navigate(`/regularization/new?anomaly_id=${encodeURIComponent(row.id)}&date=${encodeURIComponent(row.attendance_date)}&type=${encodeURIComponent(row.anomaly_type)}`);
    };

    const goSnapshot = (row) => {
        // Deep-link to daily snapshots admin filtered to this (employee, date)
        navigate(`/admin/attendance/snapshots?employee_id=${encodeURIComponent(row.employee_id)}&from_date=${encodeURIComponent(row.attendance_date)}&to_date=${encodeURIComponent(row.attendance_date)}`);
    };

    const goRequest = (row) => {
        if (!row.linked_request_id) return;
        navigate(`/regularization/requests?request_id=${encodeURIComponent(row.linked_request_id)}`);
    };

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1500px]" data-testid="anomalies-inbox-page">
            <PageHeader
                eyebrow="Attendance · E2.2 · Operational Queue"
                title="Anomalies Inbox"
                description="Triage queue for HR and Managers. Surfaces attendance exceptions that need action — late arrivals, missing check-ins/outs, unclosed breaks, consecutive lates/absents, pending regularizations. Acknowledge to clear, or create a regularization to correct."
            />

            {/* KPI header */}
            <section className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="inbox-kpis">
                <Kpi label="Open Anomalies" value={summary?.open_anomalies ?? "—"} testId="kpi-open" tone="bg-amber-50 border-amber-200 text-amber-800" />
                <Kpi label="Pending Regularizations" value={summary?.pending_regularizations ?? "—"} testId="kpi-pending" tone="bg-sky-50 border-sky-200 text-sky-800" />
                <Kpi label="Critical Anomalies" value={summary?.critical_anomalies ?? "—"} testId="kpi-critical" tone="bg-red-50 border-red-200 text-red-800" />
                <Kpi label="Overdue Regularizations" value={summary?.overdue_regularizations ?? "—"} testId="kpi-overdue" tone="bg-orange-50 border-orange-200 text-orange-800" sub="Pending > 3 days" />
                <Kpi label="Today's New" value={summary?.today_new_anomalies ?? "—"} testId="kpi-today-new" tone="bg-emerald-50 border-emerald-200 text-emerald-800" />
            </section>

            {/* Scope + Filters */}
            <div className="mt-5 bg-white rounded-xl border border-slate-200 p-4" data-testid="inbox-filters">
                <div className="flex flex-wrap gap-3 items-end">
                    <div>
                        <Label className="text-xs">Scope</Label>
                        <div className="inline-flex rounded-md overflow-hidden border border-slate-300">
                            {["mine", "team", "all"].map((s) => (
                                <button key={s} onClick={() => { setScope(s); setPage(1); }} className={`px-3 py-1.5 text-xs ${scope === s ? "bg-indigo-600 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`} data-testid={`scope-${s}`}>
                                    {s === "mine" ? "Mine" : s === "team" ? "My Team" : "All (HR)"}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">Anomaly type</Label>
                        <select className="h-10 rounded-md border border-slate-300 px-3 text-sm bg-white" value={filters.anomaly_type} onChange={(e) => { setFilters({ ...filters, anomaly_type: e.target.value }); setPage(1); }} data-testid="filter-type">
                            <option value="">All types</option>
                            {(meta.anomaly_types || []).map((t) => <option key={t} value={t}>{TYPE_LABEL[t] || t}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label className="text-xs">Severity</Label>
                        <select className="h-10 rounded-md border border-slate-300 px-3 text-sm bg-white" value={filters.severity} onChange={(e) => { setFilters({ ...filters, severity: e.target.value }); setPage(1); }} data-testid="filter-severity">
                            <option value="">All</option>
                            <option value="Critical">Critical</option>
                            <option value="High">High</option>
                            <option value="Med">Medium</option>
                            <option value="Action">Action</option>
                        </select>
                    </div>
                    <div>
                        <Label className="text-xs">Status</Label>
                        <select className="h-10 rounded-md border border-slate-300 px-3 text-sm bg-white" value={filters.status_filter} onChange={(e) => { setFilters({ ...filters, status_filter: e.target.value }); setPage(1); }} data-testid="filter-status">
                            <option value="open">Open</option>
                            <option value="regularization_pending">Regularization Pending</option>
                            <option value="acknowledged">Acknowledged</option>
                            <option value="regularized">Regularized</option>
                            <option value="closed">Closed</option>
                            <option value="all">All</option>
                        </select>
                    </div>
                    <div>
                        <Label className="text-xs">Department</Label>
                        <select className="h-10 rounded-md border border-slate-300 px-3 text-sm bg-white" value={filters.department} onChange={(e) => { setFilters({ ...filters, department: e.target.value }); setPage(1); }} data-testid="filter-department">
                            <option value="">All</option>
                            {(hrMeta.departments || []).map((d) => <option key={d.id || d.name} value={d.name}>{d.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label className="text-xs">From</Label>
                        <Input type="date" value={filters.from_date} onChange={(e) => { setFilters({ ...filters, from_date: e.target.value }); setPage(1); }} data-testid="filter-from" />
                    </div>
                    <div>
                        <Label className="text-xs">To</Label>
                        <Input type="date" value={filters.to_date} onChange={(e) => { setFilters({ ...filters, to_date: e.target.value }); setPage(1); }} data-testid="filter-to" />
                    </div>
                </div>
                {can("employee:admin") && selected.size > 0 && (
                    <div className="mt-3 flex justify-between items-center bg-indigo-50 rounded-md px-3 py-2">
                        <div className="text-xs text-indigo-700">{selected.size} selected (max 50)</div>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => setSelected(new Set())} data-testid="clear-selection">Clear</Button>
                            <Button size="sm" onClick={() => { setAckTarget(null); setAckOpen(true); }} data-testid="bulk-acknowledge-btn"><CheckCircle size={14} className="mr-1" /> Bulk Acknowledge</Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Table */}
            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-5 overflow-x-auto" data-testid="inbox-section">
                <div className="text-xs text-slate-500 mb-3">{total} anomaly(ies)</div>
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs">
                        <tr>
                            {can("employee:admin") && (
                                <th className="w-8 px-2">
                                    <input type="checkbox" checked={selected.size === rows.length && rows.length > 0} onChange={toggleSelAll} data-testid="select-all" />
                                </th>
                            )}
                            <th className="text-left px-3 py-2 font-medium">Severity</th>
                            <th className="text-left px-3 py-2 font-medium">Anomaly</th>
                            <th className="text-left px-3 py-2 font-medium">Date</th>
                            <th className="text-left px-3 py-2 font-medium">Employee</th>
                            <th className="text-left px-3 py-2 font-medium">Department</th>
                            <th className="text-left px-3 py-2 font-medium">Reporting Manager</th>
                            <th className="text-left px-3 py-2 font-medium">Shift</th>
                            <th className="text-left px-3 py-2 font-medium">Status</th>
                            <th className="text-right px-3 py-2 font-medium">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr><td colSpan={10} className="py-10 text-center text-slate-400">🎉 No anomalies for the current filters. {scope === "team" ? "Your team is on track." : ""}</td></tr>
                        )}
                        {rows.map((a) => (
                            <tr key={a.id} className="border-t border-slate-100" data-testid={`anomaly-row-${a.id}`}>
                                {can("employee:admin") && (
                                    <td className="px-2">
                                        {a.status === "open" && <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggleSel(a.id)} data-testid={`select-${a.id}`} />}
                                    </td>
                                )}
                                <td className="px-3 py-2">
                                    <Badge className={`text-[10px] border ${SEVERITY_CLS[a.severity] || SEVERITY_CLS.Low}`}>{a.severity}</Badge>
                                </td>
                                <td className="px-3 py-2 text-xs">
                                    <div className="font-medium text-slate-800">{TYPE_LABEL[a.anomaly_type] || a.anomaly_type}</div>
                                    <div className="text-[10px] text-slate-500 max-w-[260px] truncate">{a.notes}</div>
                                </td>
                                <td className="px-3 py-2 text-xs text-slate-600 font-mono">{a.attendance_date}</td>
                                <td className="px-3 py-2 text-xs">
                                    <div className="text-slate-700">{a.employee?.full_name || a.employee_id}</div>
                                    <div className="text-[10px] text-slate-400 font-mono">{a.employee?.employee_code}</div>
                                </td>
                                <td className="px-3 py-2 text-xs text-slate-600">{a.employee?.department || "—"}</td>
                                <td className="px-3 py-2 text-xs text-slate-600">
                                    {a.reporting_manager ? (
                                        <>
                                            <div>{a.reporting_manager.full_name}</div>
                                            <div className="text-[10px] text-slate-400 font-mono">{a.reporting_manager.employee_code}</div>
                                        </>
                                    ) : <span className="text-slate-400">—</span>}
                                </td>
                                <td className="px-3 py-2 text-xs text-slate-600">{a.shift?.code || "—"} {a.shift?.start_time && <span className="text-slate-400">({a.shift.start_time}–{a.shift.end_time})</span>}</td>
                                <td className="px-3 py-2 text-xs">
                                    <Badge className={`text-[10px] ${STATUS_CLS[a.status]}`}>{a.status.replace("_", " ")}</Badge>
                                    {a.attendance_status && (
                                        <div className="text-[10px] text-slate-500 mt-0.5">snap: {a.attendance_status}</div>
                                    )}
                                </td>
                                <td className="px-3 py-2 text-xs">
                                    <div className="flex justify-end gap-1">
                                        <button onClick={() => goSnapshot(a)} className="text-slate-500 hover:text-slate-800 px-1.5" title="View Snapshot" data-testid={`snapshot-${a.id}`}>
                                            <Eye size={14} />
                                        </button>
                                        {a.linked_request_id && (
                                            <button onClick={() => goRequest(a)} className="text-violet-500 hover:text-violet-700 px-1.5" title="View Linked Regularization" data-testid={`request-${a.id}`}>
                                                <FileText size={14} />
                                            </button>
                                        )}
                                        {a.status === "open" && !a.linked_request_id && (
                                            <button onClick={() => goRegularize(a)} className="text-indigo-500 hover:text-indigo-700 px-1.5" title="Create Regularization" data-testid={`regularize-${a.id}`}>
                                                <ArrowSquareOut size={14} />
                                            </button>
                                        )}
                                        {a.status === "open" && can("employee:read") && (
                                            <button onClick={() => { setAckTarget(a); setAckOpen(true); }} className="text-emerald-600 hover:text-emerald-800 px-1.5" title="Acknowledge" data-testid={`ack-${a.id}`}>
                                                <CheckCircle size={14} />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {total > 50 && (
                    <div className="mt-3 flex justify-end gap-2 text-xs items-center">
                        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
                        <span>Page {page} of {Math.ceil(total / 50)}</span>
                        <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / 50)} onClick={() => setPage((p) => p + 1)}>Next</Button>
                    </div>
                )}
            </section>

            {/* Acknowledge dialog (single + bulk) */}
            <Dialog open={ackOpen} onOpenChange={(o) => { if (!o) { setAckOpen(false); setAckRemarks(""); setAckTarget(null); } }}>
                <DialogContent data-testid="ack-dialog">
                    <DialogHeader>
                        <DialogTitle>{ackTarget ? "Acknowledge Anomaly" : `Bulk Acknowledge ${selected.size}`}</DialogTitle>
                        <DialogDescription>
                            Acknowledging accepts the snapshot as-is and closes the anomaly without creating a regularization. Audit log captures your decision + remarks.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        {ackTarget && (
                            <div className="bg-slate-50 border border-slate-200 rounded-md p-2 text-xs space-y-1">
                                <div><b>{TYPE_LABEL[ackTarget.anomaly_type]}</b> · {ackTarget.attendance_date}</div>
                                <div className="text-slate-600">{ackTarget.employee?.full_name} · {ackTarget.employee?.department}</div>
                                <div className="text-slate-500 text-[11px]">{ackTarget.notes}</div>
                            </div>
                        )}
                        <div>
                            <Label className="text-xs">Remarks (mandatory, ≥ 3 chars)</Label>
                            <Textarea rows={3} value={ackRemarks} onChange={(e) => setAckRemarks(e.target.value)} placeholder="Reviewed — known traffic delay confirmed with employee" data-testid="ack-remarks" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setAckOpen(false); setAckRemarks(""); setAckTarget(null); }}>Cancel</Button>
                        <Button onClick={doAck} data-testid="ack-confirm-btn"><CheckCircle size={14} className="mr-1" /> Acknowledge</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Kpi({ label, value, sub, tone, testId }) {
    return (
        <div className={`rounded-md border p-3 ${tone || "bg-white border-slate-200"}`} data-testid={testId}>
            <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
            <div className="text-2xl font-semibold mt-0.5">{value}</div>
            {sub && <div className="text-[10px] opacity-60 mt-0.5">{sub}</div>}
        </div>
    );
}
